import type { ITableColumn, ITableData, ITableRow } from "@/models/table"

import { HOUR_COLUMNS, getHourlyProfileMatrix } from "./hourly-profile-matrix"

type ProfileCsvValue = number | string | null

interface HourlyProfileCsvRow
  extends ITableRow, Record<string, ProfileCsvValue> {}

interface HourlyProfileToCsvTableParams {
  averages: (number | null)[][]
  hourOfDayLabel: string
  totalLabel: string
  weekStartWeekday?: number
  weekdays: string[]
}

const hourCells = (values: (number | null)[]): Record<string, number | null> =>
  Object.fromEntries(
    HOUR_COLUMNS.map((hour) => [String(hour), values[hour] ?? null])
  )

const toProfileCsvRow = (
  id: string,
  weekday: string,
  hourValues: (number | null)[],
  total: number | null
): HourlyProfileCsvRow => ({
  ...hourCells(hourValues),
  id,
  total,
  weekday,
})

export const hourlyProfileToCsvTable = ({
  averages,
  hourOfDayLabel,
  totalLabel,
  weekStartWeekday = 0,
  weekdays,
}: HourlyProfileToCsvTableParams): ITableData<HourlyProfileCsvRow> => {
  const { columnTotals, grandTotal, visibleDays } = getHourlyProfileMatrix(
    averages,
    weekStartWeekday
  )

  const columns: ITableColumn<HourlyProfileCsvRow>[] = [
    { id: 0, key: "weekday", order: 0, title: hourOfDayLabel },
    ...HOUR_COLUMNS.map((hour) => ({
      id: hour + 1,
      key: String(hour),
      order: hour + 1,
      title: String(hour),
    })),
    {
      id: HOUR_COLUMNS.length + 1,
      key: "total",
      order: HOUR_COLUMNS.length + 1,
      title: totalLabel,
    },
  ]

  return {
    columns,
    rows: [
      ...visibleDays.map((day) =>
        toProfileCsvRow(
          weekdays[day.dayIndex],
          weekdays[day.dayIndex],
          day.dayAverages,
          day.rowTotal
        )
      ),
      toProfileCsvRow("total", totalLabel, columnTotals, grandTotal),
    ],
  }
}
